import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { ErrorDialog } from '@/components/ui/ErrorDialog';
import { useAuth } from '@/lib/auth';
import { inferRepositoryRegistryFromUrl } from '@/lib/repo-registry';

import {
  adminApproveRequest,
  adminBulkApproveRequests,
  adminCancelRequest,
  adminDeleteRequest,
  adminFetchPackagePopularity,
  adminRestartRequest,
  deleteOrgRepoRequest,
  fetchIndexRequests,
  fetchOrgRepoRequests,
  restartOrgRepoRequest,
  submitIndexRequest,
  submitOrgRepoIndexRequest,
  type PackagePopularity,
} from '../api';
import { useOrg } from '../OrgProvider';
import { canManageOrg } from '../org-roles';
import { navigateTo } from '../router';
import type { IndexRequest, OrgIndexRequest } from '../types';
import {
  buildTreeRoute,
  displayPackageName,
  formatIndexedAt,
  formatIndexingProgress,
  registryLabel,
} from '../utils';
import { RegistryBadge } from './RegistryBadge';

const REGISTRY_OPTIONS = [
  { value: 'npm', label: 'npm' },
  { value: 'pypi', label: 'PyPI' },
  { value: 'pub', label: 'pub.dev' },
  { value: 'crates', label: 'crates.io' },
  { value: 'maven', label: 'Maven Central' },
] as const;

type AliasRegistry = (typeof REGISTRY_OPTIONS)[number]['value'];

interface AliasFormData {
  registry: AliasRegistry;
  name: string;
  version: string;
}

interface SourceFormData {
  registry: 'github' | 'gitlab';
  name: string; // the GitHub/GitLab URL
  version: string;
}

function parseGitRepoPath(url: string): { host: 'github' | 'gitlab'; ownerRepo: string } | null {
  try {
    const u = new URL(url);
    if (u.hostname === 'github.com') {
      const parts = u.pathname
        .replace(/^\//, '')
        .replace(/\.git$/, '')
        .split('/');
      if (parts.length >= 2) return { host: 'github', ownerRepo: `${parts[0]}/${parts[1]}` };
    }
    if (u.hostname === 'gitlab.com') {
      const parts = u.pathname
        .replace(/^\//, '')
        .replace(/\.git$/, '')
        .split('/');
      if (parts.length >= 2) return { host: 'gitlab', ownerRepo: parts.slice(0, 2).join('/') };
    }
  } catch {
    /* ignore */
  }
  return null;
}

// crates + maven resolve through the server (`/api/registry/resolve`) because
// Maven Central sends no CORS headers; the server proxy gives both one
// consistent path. Cache the in-flight promise per registry:name so the
// paired sourceUrl + versions lookups below share a single request.
const registryResolveCache = new Map<
  string,
  Promise<{ sourceUrl: string | null; versions: string[] }>
>();

function resolveViaApi(
  registry: string,
  name: string
): Promise<{ sourceUrl: string | null; versions: string[] }> {
  const key = `${registry}:${name}`;
  const hit = registryResolveCache.get(key);
  if (hit) return hit;
  const promise = (async () => {
    const params = new URLSearchParams({ registry, name });
    const res = await fetch(`/api/registry/resolve?${params.toString()}`);
    if (!res.ok) {
      registryResolveCache.delete(key);
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error ?? `Resolution failed (${res.status})`);
    }
    return (await res.json()) as { sourceUrl: string | null; versions: string[] };
  })();
  registryResolveCache.set(key, promise);
  return promise;
}

async function resolvePackageSourceUrl(
  registry: string,
  name: string,
  version?: string | null
): Promise<string | null> {
  try {
    if (registry === 'crates' || registry === 'maven') {
      const { sourceUrl } = await resolveViaApi(registry, name);
      return sourceUrl && parseGitRepoPath(sourceUrl) ? sourceUrl : null;
    }
    if (registry === 'npm') {
      const encoded = name.startsWith('@') ? name.replace('/', '%2F') : name;
      const path = version ? `${encoded}/${version}` : `${encoded}/latest`;
      const res = await fetch(`https://registry.npmjs.org/${path}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { repository?: { url?: string } };
      const raw = data.repository?.url ?? '';
      const url = raw
        .replace(/^git\+/, '')
        .replace(/\.git$/, '')
        .replace(/^git:\/\/github\.com/, 'https://github.com');
      return parseGitRepoPath(url) ? url : null;
    }
    if (registry === 'pypi') {
      const ver = version ?? 'json';
      const endpoint = version
        ? `https://pypi.org/pypi/${encodeURIComponent(name)}/${encodeURIComponent(ver)}/json`
        : `https://pypi.org/pypi/${encodeURIComponent(name)}/json`;
      const res = await fetch(endpoint);
      if (!res.ok) return null;
      const data = (await res.json()) as { info: { project_urls?: Record<string, string> } };
      const urls = data.info.project_urls ?? {};
      const url = urls['Source'] ?? urls['Homepage'] ?? urls['Repository'] ?? null;
      return url && parseGitRepoPath(url) ? url : null;
    }
    if (registry === 'pub') {
      const res = await fetch(`https://pub.dev/api/packages/${encodeURIComponent(name)}`);
      if (!res.ok) return null;
      type PubVersion = { version: string; pubspec?: { homepage?: string; repository?: string } };
      const data = (await res.json()) as { latest: PubVersion; versions: PubVersion[] };
      const entry = version
        ? (data.versions.find((v) => v.version === version) ?? data.latest)
        : data.latest;
      const url = entry.pubspec?.repository ?? entry.pubspec?.homepage ?? null;
      return url && parseGitRepoPath(url) ? url : null;
    }
  } catch {
    /* best-effort */
  }
  return null;
}

async function fetchRegistryVersions(registry: string, name: string): Promise<string[]> {
  try {
    if (registry === 'crates' || registry === 'maven') {
      const { versions } = await resolveViaApi(registry, name);
      return versions;
    }
    if (registry === 'pub') {
      const res = await fetch(`https://pub.dev/api/packages/${encodeURIComponent(name)}`);
      if (!res.ok) return [];
      const data = (await res.json()) as { versions: { version: string; published?: string }[] };
      return data.versions
        .slice()
        .reverse()
        .map((v) => v.version);
    }
    if (registry === 'npm') {
      const encoded = name.startsWith('@') ? name.replace('/', '%2F') : name;
      const res = await fetch(`https://registry.npmjs.org/${encoded}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return [];
      const data = (await res.json()) as {
        versions: Record<string, unknown>;
        time?: Record<string, string>;
      };
      const versions = Object.keys(data.versions ?? {});
      const time = data.time ?? {};
      return versions.sort((a, b) => (time[b] ?? '').localeCompare(time[a] ?? ''));
    }
    if (registry === 'pypi') {
      const res = await fetch(`https://pypi.org/pypi/${encodeURIComponent(name)}/json`);
      if (!res.ok) return [];
      const data = (await res.json()) as { releases: Record<string, { upload_time?: string }[]> };
      return Object.entries(data.releases ?? {})
        .sort((a, b) => (b[1][0]?.upload_time ?? '').localeCompare(a[1][0]?.upload_time ?? ''))
        .map(([v]) => v);
    }
  } catch {
    /* best-effort */
  }
  return [];
}

/** For monorepo URLs like .../tree/main/packages/flutter_lints, extracts the subpackage name. */
function parseMonorepoSubpackage(url: string): string | null {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\/tree\/[^/]+\/(.+)/);
    if (!m) return null;
    const parts = m[1].split('/').filter(Boolean);
    return parts[parts.length - 1] ?? null;
  } catch {
    return null;
  }
}

/**
 * Full folder path from a tree URL — `packages/flutter_lints`, not just the leaf
 * that `parseMonorepoSubpackage` returns for tag-prefix hints. GitLab nests its
 * tree routes under `/-/`, so both shapes are matched.
 */
function parseMonorepoSubpath(url: string): string | null {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\/(?:-\/)?tree\/[^/]+\/(.+)/);
    if (!m) return null;
    return m[1].split('/').filter(Boolean).join('/') || null;
  } catch {
    return null;
  }
}

/** Default package name for a folder: its last path segment. */
function subpathLeafName(subpath: string): string {
  const parts = subpath.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

async function fetchRepoReleases(sourceUrl: string, pkgNameHint?: string): Promise<string[]> {
  const parsed = parseGitRepoPath(sourceUrl);
  if (!parsed) return [];
  const subPkg = pkgNameHint ?? parseMonorepoSubpackage(sourceUrl);
  try {
    let tags: string[] = [];
    if (parsed.host === 'github') {
      const res = await fetch(
        `https://api.github.com/repos/${parsed.ownerRepo}/releases?per_page=40`,
        {
          headers: { Accept: 'application/vnd.github+json' },
        }
      );
      if (!res.ok) return [];
      const data = (await res.json()) as { tag_name: string; draft: boolean }[];
      tags = data.filter((r) => !r.draft).map((r) => r.tag_name);
    } else if (parsed.host === 'gitlab') {
      const res = await fetch(
        `https://gitlab.com/api/v4/projects/${encodeURIComponent(parsed.ownerRepo)}/releases?per_page=40`
      );
      if (!res.ok) return [];
      const data = (await res.json()) as { tag_name: string }[];
      tags = data.map((r) => r.tag_name);
    }
    // For monorepo packages, filter by package-name prefix (e.g. flutter_lints-v6.0.0)
    if (subPkg && tags.length > 0) {
      const prefixed = tags.filter((t) => t.startsWith(`${subPkg}-`) || t.startsWith(`${subPkg}/`));
      if (prefixed.length > 0) return prefixed;
    }
    return tags;
  } catch {
    /* best-effort */
  }
  return [];
}

function ApproveDialog({
  request,
  isApproving,
  onConfirm,
  onClose,
}: {
  request: IndexRequest;
  isApproving: boolean;
  onConfirm: (
    alias: AliasFormData | null,
    source: SourceFormData | null,
    sourceSubpath: string | null
  ) => void;
  onClose: () => void;
}) {
  const isPackageRequest = request.registry !== 'github' && request.registry !== 'gitlab';

  // Source repo
  const [sourceUrl, setSourceUrl] = useState('');
  const [isResolvingSourceUrl, setIsResolvingSourceUrl] = useState(false);
  const [sourceReleases, setSourceReleases] = useState<string[]>([]);
  const [userSelectedVersion, setUserSelectedVersion] = useState<string | null>(null);
  // Whether to index from a GitHub/GitLab repo instead of the package registry
  const [useGitOverride, setUseGitOverride] = useState(false);
  // Folder inside the repo to index instead of the whole tree (monolith case)
  const [sourceSubpath, setSourceSubpath] = useState(request.source_subpath ?? '');
  // Tracks whether the alias name was auto-filled from the folder, so typing a
  // different folder keeps updating it but an operator's own name is never lost.
  const aliasNameIsAuto = useRef(true);

  // Alias (for npm/pypi/pub: pre-filled; for git: optional checkbox)
  const [includeAlias, setIncludeAlias] = useState(isPackageRequest);
  const [aliasRegistry, setAliasRegistry] = useState<AliasRegistry>(
    REGISTRY_OPTIONS.some((o) => o.value === request.registry)
      ? (request.registry as AliasRegistry)
      : 'npm'
  );
  const [aliasName, setAliasName] = useState(isPackageRequest ? request.name : '');
  const [aliasVersion, setAliasVersion] = useState(isPackageRequest ? (request.version ?? '') : '');

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isApproving) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isApproving, onClose]);

  // On mount, auto-resolve source URL from package registry
  useEffect(() => {
    if (!isPackageRequest) return;
    let cancelled = false;
    setIsResolvingSourceUrl(true);
    void resolvePackageSourceUrl(request.registry, request.name, request.version)
      .then((url) => {
        if (!cancelled && url) setSourceUrl(url);
      })
      .finally(() => {
        if (!cancelled) setIsResolvingSourceUrl(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch releases when sourceUrl looks like a valid git URL
  useEffect(() => {
    const url = sourceUrl.trim();
    const parsed = parseGitRepoPath(url);
    if (!url || !parsed) return;

    let cancelled = false;
    void fetchRepoReleases(url, isPackageRequest ? request.name : undefined).then((releases) => {
      if (!cancelled) {
        setSourceReleases(releases);
        setUserSelectedVersion(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sourceUrl, isPackageRequest, request.name]);

  // A pasted tree URL already names the folder — lift it into the field rather
  // than making the operator retype it. Only fills a blank field.
  useEffect(() => {
    const fromUrl = parseMonorepoSubpath(sourceUrl.trim());
    if (fromUrl) setSourceSubpath((current) => current.trim() || fromUrl);
  }, [sourceUrl]);

  // A folder is its own package and needs its own name, so default it to the
  // folder's last segment — `services/billing` becomes `billing`.
  useEffect(() => {
    const trimmed = sourceSubpath.trim();
    if (!trimmed || !aliasNameIsAuto.current) return;
    setIncludeAlias(true);
    setAliasName(subpathLeafName(trimmed));
  }, [sourceSubpath]);

  const isValidSourceUrl = Boolean(parseGitRepoPath(sourceUrl.trim()));
  const effectiveReleases = isValidSourceUrl ? sourceReleases : [];
  const selectedVersion = userSelectedVersion ?? effectiveReleases[0] ?? '';
  // Alias version is always the registry version (e.g. npm 3.10.1), not the
  // git tag (e.g. v3.10.1) — those are independent.
  const effectiveAliasVersion = aliasVersion;

  const inferredSourceRegistry = sourceUrl ? inferRepositoryRegistryFromUrl(sourceUrl) : null;

  const trimmedSubpath = sourceSubpath.trim();
  const canConfirmAlias =
    !includeAlias || (aliasName.trim() !== '' && effectiveAliasVersion.trim() !== '');
  // If git override is chosen, require a valid URL + version
  const canConfirmOverride =
    !isPackageRequest || !useGitOverride || (isValidSourceUrl && selectedVersion.trim() !== '');
  // A folder is stored under the alias name; without one it would land on the
  // repository name and overwrite the sibling folders of the same monolith.
  const canConfirmSubpath = !trimmedSubpath || (includeAlias && aliasName.trim() !== '');
  const canConfirm = !isApproving && canConfirmAlias && canConfirmOverride && canConfirmSubpath;

  const handleConfirm = () => {
    const alias: AliasFormData | null =
      includeAlias && aliasName.trim() && effectiveAliasVersion.trim()
        ? { registry: aliasRegistry, name: aliasName.trim(), version: effectiveAliasVersion.trim() }
        : null;
    // Only pass source when using git override — otherwise pipeline downloads from registry
    const source: SourceFormData | null =
      isPackageRequest && useGitOverride && inferredSourceRegistry && selectedVersion
        ? { registry: inferredSourceRegistry, name: sourceUrl.trim(), version: selectedVersion }
        : !isPackageRequest && inferredSourceRegistry && selectedVersion
          ? { registry: inferredSourceRegistry, name: sourceUrl.trim(), version: selectedVersion }
          : null;
    onConfirm(alias, source, trimmedSubpath || null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={!isApproving ? onClose : undefined}
      />
      <div className="relative w-full max-w-md overflow-hidden border border-rule bg-panel shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-rule-soft px-6 py-5">
          <div>
            <p className="text-2xs font-medium uppercase tracking-[0.18em] text-dim">
              Approve indexing request
            </p>
            <div className="mt-1 flex items-center gap-2">
              <h3 className="max-w-xs truncate text-lg font-semibold text-ink" title={request.name}>
                {canonicalPackageName(request.registry, request.name)}
              </h3>
              {!isPackageRequest && parseGitRepoPath(request.name) && (
                <a
                  href={request.name}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-xs text-accent hover:text-accent/90"
                >
                  GitHub ↗
                </a>
              )}
            </div>
            <p className="mt-0.5 font-mono text-xs text-dim">
              {request.registry}
              {request.version ? ` · ${request.version}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isApproving}
            className="p-1 text-dim transition-colors hover:bg-bg hover:text-link disabled:opacity-40"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5">
          {/* ── Source mode picker for pub/npm/pypi ── */}
          {isPackageRequest ? (
            <div className="space-y-3">
              {/* Mode radio */}
              <div className="space-y-2">
                <label
                  className={`flex cursor-pointer items-start gap-3 border p-3 transition-colors ${
                    !useGitOverride
                      ? 'border-accent bg-accent-soft'
                      : 'border-rule hover:border-rule'
                  }`}
                >
                  <input
                    type="radio"
                    checked={!useGitOverride}
                    onChange={() => setUseGitOverride(false)}
                    className="mt-0.5 h-4 w-4 text-accent"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">
                      Download from {registryLabel(request.registry)}
                    </p>
                    <p className="mt-0.5 font-mono text-xs text-dim">
                      {request.name}@{request.version}
                    </p>
                  </div>
                </label>

                <label
                  className={`flex cursor-pointer items-start gap-3 border p-3 transition-colors ${
                    useGitOverride
                      ? 'border-accent bg-accent-soft'
                      : 'border-rule hover:border-rule'
                  }`}
                >
                  <input
                    type="radio"
                    checked={useGitOverride}
                    onChange={() => setUseGitOverride(true)}
                    className="mt-0.5 h-4 w-4 text-accent"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">Index from GitHub / GitLab repo</p>
                    <p className="mt-0.5 text-xs text-dim">
                      Override — index a specific repo + tag instead
                    </p>
                  </div>
                </label>
              </div>

              {/* Git override fields */}
              {useGitOverride && (
                <div className="space-y-3 border border-rule bg-bg p-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label htmlFor="approve-source-url" className="text-xs font-medium text-dim">
                        Repository URL
                      </label>
                      {isValidSourceUrl && (
                        <a
                          href={sourceUrl.trim()}
                          target="_blank"
                          rel="noreferrer"
                          className="text-2xs text-accent hover:text-accent/90"
                        >
                          Open ↗
                        </a>
                      )}
                    </div>
                    <input
                      id="approve-source-url"
                      value={sourceUrl}
                      onChange={(e) => setSourceUrl(e.target.value)}
                      placeholder={
                        isResolvingSourceUrl
                          ? 'Looking up repository…'
                          : 'https://github.com/owner/repo'
                      }
                      disabled={isResolvingSourceUrl}
                      className="lc-input w-full font-mono text-sm disabled:opacity-50"
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {sourceUrl.trim() && !parseGitRepoPath(sourceUrl.trim()) && (
                      <p className="text-xs text-warn">Enter a valid GitHub or GitLab URL.</p>
                    )}
                  </div>

                  {isValidSourceUrl && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-dim">
                        Tag / branch <span className="text-del">*</span>
                      </label>
                      {effectiveReleases.length > 0 ? (
                        <>
                          <div className="max-h-36 overflow-y-auto border border-rule-soft">
                            {effectiveReleases.map((tag, i) => (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => setUserSelectedVersion(tag)}
                                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-accent-soft ${
                                  selectedVersion === tag
                                    ? 'bg-accent-soft font-medium text-accent'
                                    : 'text-link'
                                }`}
                              >
                                <span className="font-mono text-xs">{tag}</span>
                                {i === 0 && <span className="text-2xs text-dim">latest</span>}
                              </button>
                            ))}
                          </div>
                          <details className="text-2xs text-dim">
                            <summary className="cursor-pointer hover:text-link">
                              Enter manually
                            </summary>
                            <input
                              value={userSelectedVersion ?? ''}
                              onChange={(e) => setUserSelectedVersion(e.target.value)}
                              placeholder="e.g. v1.2.3"
                              className="lc-input mt-1.5 w-full font-mono text-sm"
                            />
                          </details>
                        </>
                      ) : (
                        <>
                          <input
                            value={userSelectedVersion ?? ''}
                            onChange={(e) => setUserSelectedVersion(e.target.value)}
                            placeholder={
                              parseMonorepoSubpackage(sourceUrl.trim())
                                ? `e.g. ${parseMonorepoSubpackage(sourceUrl.trim())}-v${request.version ?? '1.0.0'}`
                                : 'e.g. v1.2.3'
                            }
                            className="lc-input w-full font-mono text-sm"
                          />
                          {parseMonorepoSubpackage(sourceUrl.trim()) && (
                            <p className="text-2xs text-dim">
                              Monorepo — tags are usually prefixed, e.g.{' '}
                              {parseMonorepoSubpackage(sourceUrl.trim())}-v
                              {request.version ?? 'X.Y.Z'}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {isValidSourceUrl && (
                    <div className="space-y-1.5">
                      <label
                        className="text-xs font-medium text-dim"
                        htmlFor="approve-source-subpath"
                      >
                        Folder <span className="text-dim">(optional)</span>
                      </label>
                      <input
                        id="approve-source-subpath"
                        value={sourceSubpath}
                        onChange={(e) => setSourceSubpath(e.target.value)}
                        placeholder="e.g. services/billing"
                        className="lc-input w-full font-mono text-sm"
                        autoComplete="off"
                        spellCheck={false}
                      />
                      <p className="text-2xs text-dim">
                        {trimmedSubpath
                          ? 'Only this folder is indexed, as its own package named by the alias below.'
                          : 'Leave blank to index the whole repository.'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}

          {/* ── Alias section ── */}
          <>
            <label className="flex cursor-pointer items-center gap-2.5">
              <input
                type="checkbox"
                checked={includeAlias}
                onChange={(e) => setIncludeAlias(e.target.checked)}
                className="h-4 w-4 rounded border-rule text-accent focus:ring-accent"
              />
              <span className="text-sm font-medium text-link">Register a package alias</span>
            </label>
            {includeAlias ? (
              <div className="space-y-3 border border-rule-soft bg-bg p-4">
                <div className="flex gap-2">
                  {REGISTRY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setAliasRegistry(opt.value)}
                      className={`border px-3 py-1.5 text-xs font-medium transition-colors ${
                        aliasRegistry === opt.value
                          ? 'border-accent bg-accent-soft text-accent'
                          : 'border-rule bg-panel text-link hover:border-rule'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    value={aliasName}
                    onChange={(e) => {
                      aliasNameIsAuto.current = false;
                      setAliasName(e.target.value);
                    }}
                    placeholder={
                      aliasRegistry === 'npm'
                        ? 'e.g. react'
                        : aliasRegistry === 'pypi'
                          ? 'e.g. requests'
                          : 'e.g. provider'
                    }
                    className="lc-input min-w-0 flex-1 font-mono text-sm"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <input
                    value={aliasVersion}
                    onChange={(e) => setAliasVersion(e.target.value)}
                    placeholder="e.g. 1.2.3"
                    className="lc-input w-28 font-mono text-sm"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-dim">
                Optionally add a registry alias so this repository can be resolved by package name.
              </p>
            )}
          </>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-rule-soft px-6 py-4">
          <Button size="sm" variant="secondary" onClick={onClose} disabled={isApproving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleConfirm} disabled={!canConfirm}>
            {isApproving ? 'Approving…' : '✓ Approve'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  pending_approval: 'bg-bg text-dim',
  pending: 'bg-warn/15 text-warn',
  indexing: 'bg-accent-soft text-accent',
  done: 'bg-good/15 text-good',
  failed: 'bg-del/15 text-del',
};

function StatusBadge({ status }: { status: string }) {
  const label = status === 'pending_approval' ? 'awaiting approval' : status;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] ?? ''}`}
    >
      {status === 'indexing' && (
        <span className="mr-1.5 h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
      )}
      {label}
    </span>
  );
}

function popularityKey(registry: string, name: string): string {
  return `${registry}:${name.toLowerCase()}`;
}

function formatDownloads(count: number): string {
  if (count >= 1_000_000_000) return `${(count / 1_000_000_000).toFixed(1)}B`;
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(0)}k`;
  return String(count);
}

/**
 * Monthly downloads, as a sanity check on whether a queued package is something
 * real or a typo-squat nobody installs.
 *
 * A missing number is shown as "no data" rather than as zero: Maven Central
 * publishes no statistics at all, so treating absence as unpopularity would
 * mislabel every Java package in the queue.
 */
function PopularityBadge({ popularity }: { popularity: PackagePopularity | undefined }) {
  if (!popularity) {
    return <span className="font-mono text-2xs text-dim/60">…</span>;
  }

  if (popularity.monthlyDownloads === null) {
    return (
      <span
        className="text-2xs text-dim/70"
        title={popularity.unavailableReason ?? 'No download data'}
      >
        no data
      </span>
    );
  }

  const downloads = popularity.monthlyDownloads;
  // Thresholds are a triage aid, not a verdict: low means "look closer".
  const tone = downloads >= 100_000 ? 'text-good' : downloads >= 1_000 ? 'text-dim' : 'text-warn';
  const detail = [
    `${downloads.toLocaleString()} downloads in the last 30 days`,
    popularity.totalDownloads ? `${popularity.totalDownloads.toLocaleString()} all time` : null,
    popularity.likes ? `${popularity.likes.toLocaleString()} likes` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <span className={`font-mono text-2xs ${tone}`} title={detail} data-testid="request-popularity">
      {formatDownloads(downloads)}/mo
    </span>
  );
}

/**
 * Warns that approving this request would re-index something the catalog
 * already has. Only shown while the request is still open — once it is done the
 * package being in the catalog is the expected outcome, not a duplicate.
 */
function DuplicateBadge({ request }: { request: IndexRequest }) {
  if (request.status !== 'pending_approval') return null;

  if (request.already_indexed) {
    return (
      <span
        data-testid="request-already-indexed"
        className="border border-warn/40 bg-warn/10 px-1.5 py-[1px] text-2xs font-medium text-warn"
        title={`${request.version} is already in the catalog — approving this would re-index it.`}
      >
        already indexed
      </span>
    );
  }

  const otherVersions = request.indexed_version_count ?? 0;
  if (otherVersions > 0) {
    return (
      <span
        data-testid="request-other-versions-indexed"
        className="border border-rule px-1.5 py-[1px] text-2xs font-medium text-dim"
        title={`${otherVersions} other version${otherVersions === 1 ? '' : 's'} of this package ${otherVersions === 1 ? 'is' : 'are'} already indexed; this version is not.`}
      >
        {otherVersions} other version{otherVersions === 1 ? '' : 's'} indexed
      </span>
    );
  }

  return null;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function getProgressText(
  request: Pick<IndexRequest, 'status' | 'error' | 'progress_message' | 'progress_step'>
): string | null {
  if (request.error) return request.error;
  const base = request.progress_message?.trim();
  if (request.status === 'indexing' && base) {
    return formatIndexingProgress(request.progress_step, base) ?? 'Indexing in progress.';
  }
  if (request.status === 'pending_approval') return base ?? 'Waiting for admin approval.';
  if (request.status === 'pending') return base ?? 'Queued for indexing.';
  if (request.status === 'done') return base ?? 'Indexing complete.';
  if (request.status === 'failed') return base ?? 'Indexing failed.';
  return base ?? null;
}

function getOrgProgressText(
  request: Pick<OrgIndexRequest, 'status' | 'error' | 'progress_message' | 'progress_step'>
): string | null {
  if (request.error) return request.error;
  const base = request.progress_message?.trim();
  if (request.status === 'indexing' && base) {
    return formatIndexingProgress(request.progress_step, base) ?? 'Indexing in progress.';
  }
  if (request.status === 'pending') return base ?? 'Queued for indexing.';
  if (request.status === 'done') return base ?? 'Indexing complete.';
  if (request.status === 'failed') return base ?? 'Indexing failed.';
  return base ?? null;
}

function formatDuration(start: string, end?: string | null): string | null {
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return null;

  const totalSeconds = Math.max(1, Math.round((endMs - startMs) / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0 && seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
}

function formatCost(value: number): string {
  return value < 0.01 ? `$${value.toFixed(4)}` : `$${value.toFixed(3)}`;
}

function getRequestTimingItems(
  request: Pick<IndexRequest, 'requested_at' | 'started_at' | 'completed_at' | 'index_cost'>
): string[] {
  const items: string[] = [];

  if (request.started_at) {
    const wait = formatDuration(request.requested_at, request.started_at);
    if (wait) items.push(`waited ${wait}`);
  }

  if (request.started_at) {
    const runtime = formatDuration(request.started_at, request.completed_at);
    if (runtime) {
      items.push(request.completed_at ? `ran ${runtime}` : `running ${runtime}`);
    }
  }

  if (request.index_cost != null) {
    items.push(`cost ${formatCost(request.index_cost)}`);
  }

  return items;
}

function getOrgRequestTimingItems(
  request: Pick<OrgIndexRequest, 'requested_at' | 'started_at' | 'completed_at' | 'index_cost'>
): string[] {
  const items: string[] = [];

  if (request.started_at) {
    const wait = formatDuration(request.requested_at, request.started_at);
    if (wait) items.push(`waited ${wait}`);
  }

  if (request.started_at) {
    const runtime = formatDuration(request.started_at, request.completed_at);
    if (runtime) {
      items.push(request.completed_at ? `ran ${runtime}` : `running ${runtime}`);
    }
  }

  if (request.index_cost != null) {
    items.push(`cost ${formatCost(request.index_cost)}`);
  }

  return items;
}

function canonicalPackageName(registry: string, name: string): string {
  if (registry === 'github' || registry === 'gitlab') {
    const parsed = parseGitRepoPath(name);
    if (parsed) return parsed.ownerRepo;
  }
  return name;
}

function getPackageRoute(
  request: Pick<IndexRequest, 'status' | 'registry' | 'name' | 'version'>
): string | null {
  if (request.status !== 'done' || !request.version) return null;
  return buildTreeRoute({
    registry: request.registry,
    name: canonicalPackageName(request.registry, request.name),
    version: request.version,
  });
}

function getOrgPackageRoute(
  request: Pick<OrgIndexRequest, 'status' | 'registry' | 'name' | 'version'>
): string | null {
  if (request.status !== 'done' || !request.version) return null;
  return buildTreeRoute({
    registry: request.registry,
    name: canonicalPackageName(request.registry, request.name),
    version: request.version,
  });
}

export function CommonPackageRequestsPanel({
  sectionId,
  isAdmin = false,
  hideForm = false,
  filter,
  emptyLabel,
  title = 'Recent indexing requests',
}: {
  sectionId?: string;
  isAdmin?: boolean;
  hideForm?: boolean;
  filter?: (request: IndexRequest) => boolean;
  emptyLabel?: string;
  title?: string;
}) {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [docsUrl, setDocsUrl] = useState('');
  const [inputRegistry, setInputRegistry] = useState<
    'git' | 'pub' | 'npm' | 'pypi' | 'crates' | 'maven'
  >('git');
  const [resolvedUrl, setResolvedUrl] = useState('');
  const [resolvedReleases, setResolvedReleases] = useState<string[]>([]);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const resolveAbortRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [requests, setRequests] = useState<IndexRequest[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [restartingId, setRestartingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [stoppingId, setStoppingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [approveDialogRequest, setApproveDialogRequest] = useState<IndexRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRecentOpen, setIsRecentOpen] = useState(true);
  const [statusFilter, setStatusFilter] = useState<IndexRequest['status'] | 'all'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const [popularity, setPopularity] = useState<Map<string, PackagePopularity>>(new Map());
  const [isLoadingPopularity, setIsLoadingPopularity] = useState(false);
  /** Packages already asked about, so re-renders don't re-fetch them. */
  const popularityRequestedRef = useRef<Set<string>>(new Set());

  // Reset resolved state when registry or name changes. Crucially,
  // also clear `version` so the post-resolve auto-select picks up the
  // new latest version instead of sticking with whatever the previous
  // package happened to leave behind.
  useEffect(() => {
    setResolvedUrl('');
    setResolvedReleases([]);
    setResolveError(null);
    setVersion('');
    if (resolveAbortRef.current) clearTimeout(resolveAbortRef.current);

    if (inputRegistry === 'git' || !name.trim()) return;

    // Maven packages are addressed by coordinate, not bare name — catch the
    // common mistake before hitting the resolver.
    if (inputRegistry === 'maven' && !name.includes(':')) {
      setResolveError('Maven needs groupId:artifactId — e.g. com.google.code.gson:gson');
      return;
    }

    setIsResolving(true);
    resolveAbortRef.current = setTimeout(async () => {
      try {
        const [url, versions] = await Promise.all([
          resolvePackageSourceUrl(inputRegistry, name.trim()),
          fetchRegistryVersions(inputRegistry, name.trim()),
        ]);
        if (!url && versions.length === 0) {
          setResolveError('Package not found on the registry.');
          return;
        }
        setResolvedUrl(url ?? '');
        setResolvedReleases(versions);
        // Auto-select the latest (versions are sorted newest-first
        // by fetchRegistryVersions). Always fires — we cleared version
        // above, so there's no stale-guard issue.
        if (versions.length > 0) setVersion(versions[0]);
      } catch {
        setResolveError('Resolution failed — check the package name.');
      } finally {
        setIsResolving(false);
      }
    }, 600);

    return () => {
      if (resolveAbortRef.current) clearTimeout(resolveAbortRef.current);
    };
  }, [name, inputRegistry]);

  const loadRequests = useCallback(async () => {
    try {
      setRequests(await fetchIndexRequests());
    } catch {
      // Silently retry on next poll
    }
  }, []);

  useEffect(() => {
    void loadRequests();
    const interval = setInterval(() => void loadRequests(), 5_000);
    return () => clearInterval(interval);
  }, [loadRequests]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const token = await getToken();
      if (!token) return;
      await adminDeleteRequest(id, token);
      setRequests((prev) => prev.filter((request) => request.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete request.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestart = async (id: string) => {
    setRestartingId(id);
    try {
      const token = await getToken();
      if (!token) return;
      await adminRestartRequest(id, token);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restart request.');
    } finally {
      setRestartingId(null);
    }
  };

  const handleStop = async (id: string) => {
    setStoppingId(id);
    try {
      const token = await getToken();
      if (!token) return;
      await adminCancelRequest(id, token);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop request.');
    } finally {
      setStoppingId(null);
    }
  };

  const handleApprove = async (
    id: string,
    alias: AliasFormData | null,
    source: SourceFormData | null,
    sourceSubpath: string | null
  ) => {
    setApprovingId(id);
    try {
      const token = await getToken();
      if (!token) return;
      await adminApproveRequest(id, token, alias ?? undefined, source ?? undefined, sourceSubpath);
      setApproveDialogRequest(null);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request.');
    } finally {
      setApprovingId(null);
    }
  };

  const handleBulkApprove = async (ids: string[]) => {
    if (ids.length === 0) return;
    setIsBulkApproving(true);
    setBulkResult(null);
    try {
      const token = await getToken();
      if (!token) return;
      const result = await adminBulkApproveRequests(ids, token);
      // Partial failure is the expected case, not an edge case: folder requests
      // need a per-request alias and are rejected by the bulk path by design.
      setBulkResult(
        result.failed.length === 0
          ? `Approved ${result.approved} request${result.approved === 1 ? '' : 's'}.`
          : `Approved ${result.approved}; ${result.failed.length} could not be approved in bulk — ${result.failed[0].error}`
      );
      setSelectedIds(new Set());
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve requests.');
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim() || !version.trim()) return;

    // For package registries (pub/npm/pypi), submit directly — the pipeline
    // downloads from the registry using fetchPubSource/fetchNpmSource/etc.
    // No GitHub URL needed or used.
    if (inputRegistry !== 'git') {
      setIsSubmitting(true);
      setError(null);
      try {
        await submitIndexRequest({
          registry: inputRegistry,
          name: name.trim(),
          version: version.trim(),
          docsUrl: docsUrl.trim() || undefined,
        });
        setName('');
        setVersion('');
        setDocsUrl('');
        setResolvedUrl('');
        setResolvedReleases([]);
        setIsRecentOpen(true);
        await loadRequests();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to submit request.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Git registry: need a valid GitHub/GitLab URL
    const inferredRegistry = inferRepositoryRegistryFromUrl(name.trim());
    if (!inferredRegistry) {
      setError('Enter a valid GitHub or GitLab repository URL.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await submitIndexRequest({
        registry: inferredRegistry,
        name: name.trim(),
        version: version.trim(),
      });
      setName('');
      setVersion('');
      setIsRecentOpen(true);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to submit request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scopedRequests = filter ? requests.filter(filter) : requests;
  const activeCount = scopedRequests.filter(
    (request) =>
      request.status === 'pending_approval' ||
      request.status === 'pending' ||
      request.status === 'indexing'
  ).length;

  // The approval backlog runs to the hundreds, so an unfiltered scroll is not a
  // way to work through it. Counts come from scopedRequests, not the filtered
  // list, so each tab shows how much is behind it before you switch.
  const statusCounts = scopedRequests.reduce<Record<string, number>>((acc, request) => {
    acc[request.status] = (acc[request.status] ?? 0) + 1;
    return acc;
  }, {});
  const statusTabs = (
    [
      ['all', 'All'],
      ['pending_approval', 'Awaiting approval'],
      ['pending', 'Queued'],
      ['indexing', 'Indexing'],
      ['done', 'Done'],
      ['failed', 'Failed'],
    ] as const
  ).filter(([value]) => value === 'all' || (statusCounts[value] ?? 0) > 0);
  const visibleRequests =
    statusFilter === 'all'
      ? scopedRequests
      : scopedRequests.filter((request) => request.status === statusFilter);

  // Download counts for whatever is on screen, fetched once per package and
  // kept in a ref so the state update this triggers can't loop back into
  // another fetch. Advisory data: a failure leaves the queue fully usable.
  useEffect(() => {
    if (!isAdmin) return;
    const targets: Array<{ registry: string; name: string }> = [];
    const claimed: string[] = [];
    for (const request of visibleRequests) {
      if (request.status === 'done' || request.status === 'failed') continue;
      const name = canonicalPackageName(request.registry, request.name);
      const key = popularityKey(request.registry, name);
      if (popularityRequestedRef.current.has(key)) continue;
      popularityRequestedRef.current.add(key);
      claimed.push(key);
      targets.push({ registry: request.registry, name });
    }
    if (targets.length === 0) return;

    let cancelled = false;
    setIsLoadingPopularity(true);
    void (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const results = await adminFetchPackagePopularity(targets, token);
        if (cancelled) return;
        setPopularity((prev) => {
          const next = new Map(prev);
          for (const result of results)
            next.set(popularityKey(result.registry, result.name), result);
          return next;
        });
      } catch {
        // Release the claim so switching tabs retries rather than showing a
        // permanently blank column after one bad network moment.
        for (const key of claimed) popularityRequestedRef.current.delete(key);
      } finally {
        if (!cancelled) setIsLoadingPopularity(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, visibleRequests, getToken]);

  // Bulk approval targets only what is both selected and still awaiting
  // approval, so a stale selection can't re-approve something already running.
  const approvableSelectedIds = visibleRequests
    .filter((request) => request.status === 'pending_approval' && selectedIds.has(request.id))
    .map((request) => request.id);
  const approvableVisible = visibleRequests.filter(
    (request) => request.status === 'pending_approval'
  );

  return (
    <div id={sectionId} className="space-y-4">
      {hideForm ? null : (
        <form
          onSubmit={handleSubmit}
          className="lc-surface space-y-4 p-5"
          data-testid="request-form"
        >
          {/* Registry selector */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-dim">
              1. Choose the canonical source
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(['git', 'pub', 'npm', 'pypi', 'crates', 'maven'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    setInputRegistry(r);
                    setName('');
                    setVersion('');
                    setDocsUrl('');
                  }}
                  className={`border px-3 py-1 text-xs font-medium transition-colors ${
                    inputRegistry === r
                      ? 'border-accent bg-accent-soft text-accent'
                      : 'border-rule text-dim hover:border-rule'
                  }`}
                >
                  {r === 'git' ? 'GitHub / GitLab' : registryLabel(r)}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-dim">
              Registry packages are verified by name and downloaded directly from the selected
              registry. Repository requests require a public URL and release tag.
            </p>
          </div>

          <p className="text-xs font-semibold uppercase tracking-wider text-dim">
            2. Pin the snapshot
          </p>
          <div className="grid gap-4 sm:grid-cols-[1fr,minmax(9rem,11rem)] sm:items-end">
            <div className="space-y-1.5">
              <label htmlFor="browse-request-name" className="text-sm font-medium text-link">
                {inputRegistry === 'git'
                  ? 'Repository URL'
                  : inputRegistry === 'maven'
                    ? 'Maven coordinate'
                    : 'Package name'}
              </label>
              <input
                id="browse-request-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  inputRegistry === 'git'
                    ? 'https://github.com/owner/repo'
                    : inputRegistry === 'pub'
                      ? 'e.g. flutter_markdown_plus'
                      : inputRegistry === 'npm'
                        ? 'e.g. zod or @trpc/server'
                        : inputRegistry === 'crates'
                          ? 'e.g. reqwest'
                          : inputRegistry === 'maven'
                            ? 'e.g. com.google.code.gson:gson'
                            : 'e.g. fastapi'
                }
                className="lc-input"
                required
              />
            </div>

            {/* Version: releases dropdown for resolved packages, plain input otherwise */}
            <div className="space-y-1.5">
              <label htmlFor="browse-request-version" className="text-sm font-medium text-link">
                {inputRegistry === 'git' ? 'Release tag' : 'Version / tag'}
              </label>
              {resolvedReleases.length > 0 ? (
                <select
                  id="browse-request-version"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="lc-input"
                  required
                >
                  {resolvedReleases.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="browse-request-version"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder={inputRegistry === 'git' ? 'e.g. v8.0.8' : 'e.g. 0.4.0'}
                  className="lc-input w-32"
                  required
                />
              )}
            </div>
          </div>

          {/* Resolution status for package registries */}
          {inputRegistry !== 'git' && (
            <div className="text-xs">
              {isResolving && <span className="text-dim">Looking up repository…</span>}
              {!isResolving && resolvedUrl && (
                <span className="text-good">
                  ✓ Source repo:{' '}
                  <a
                    href={resolvedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium underline underline-offset-2 hover:text-good/90"
                  >
                    {resolvedUrl.replace('https://', '')}
                  </a>
                  <span className="ml-1.5 text-dim">
                    (we download directly from {registryLabel(inputRegistry)})
                  </span>
                </span>
              )}
              {!isResolving && resolveError && <span className="text-warn">{resolveError}</span>}
              {!isResolving && !resolvedUrl && !resolveError && name.trim() && (
                <span className="text-dim">
                  Enter a package name to auto-resolve the source repository.
                </span>
              )}
            </div>
          )}

          {/* Optional docs URL override — only meaningful for package registries,
            which crawl a docs site alongside the source. Blank = auto-discover. */}
          {inputRegistry !== 'git' && (
            <div className="space-y-1.5">
              <label htmlFor="browse-request-docs-url" className="text-sm font-medium text-link">
                Docs URL <span className="font-normal text-dim">(optional)</span>
              </label>
              <input
                id="browse-request-docs-url"
                type="url"
                value={docsUrl}
                onChange={(e) => setDocsUrl(e.target.value)}
                placeholder="e.g. https://docs.pydantic.dev/latest/"
                className="lc-input"
              />
              <p className="text-xs text-dim">
                Leave blank to auto-detect the docs site from the package homepage.
              </p>
            </div>
          )}

          {error && <p className="text-sm text-del">{error}</p>}

          <div className="flex items-center justify-between">
            <p className="text-xs text-dim">
              {inputRegistry === 'git'
                ? 'The release tag and index date will identify this snapshot in future answers.'
                : `Identity is checked against ${registryLabel(inputRegistry)}; this exact version is then indexed and dated.`}
            </p>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting || !name.trim() || !version.trim()}
              data-testid="request-submit-btn"
            >
              {isSubmitting ? 'Submitting…' : 'Request'}
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setIsRecentOpen((open) => !open)}
            className="flex items-center gap-2 text-sm font-medium text-link transition-colors hover:text-ink"
            aria-expanded={isRecentOpen}
            aria-controls="recent-package-requests-panel"
          >
            <span>{title}</span>
            {activeCount > 0 ? (
              <span data-testid="request-active-count" className="chip-accent">
                {activeCount} active
              </span>
            ) : null}
            <ChevronIcon open={isRecentOpen} />
          </button>
        </div>
        {isRecentOpen && statusTabs.length > 1 ? (
          <div className="flex flex-wrap gap-1.5" data-testid="request-status-filter">
            {statusTabs.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatusFilter(value)}
                aria-pressed={statusFilter === value}
                className={`border px-2.5 py-1 text-xs font-medium transition-colors ${
                  statusFilter === value
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-rule text-dim hover:text-ink'
                }`}
              >
                {label}
                <span className="ml-1.5 font-mono text-2xs">
                  {value === 'all' ? scopedRequests.length : (statusCounts[value] ?? 0)}
                </span>
              </button>
            ))}
          </div>
        ) : null}
        {isRecentOpen && isAdmin && approvableVisible.length > 0 ? (
          <div
            className="flex flex-wrap items-center gap-2 border border-rule bg-bg px-3 py-2"
            data-testid="bulk-approve-bar"
          >
            <label className="flex items-center gap-2 text-xs text-dim">
              <input
                type="checkbox"
                checked={
                  approvableSelectedIds.length > 0 &&
                  approvableSelectedIds.length === approvableVisible.length
                }
                // Partially-selected is its own state: without this the box
                // reads as "nothing selected" while a subset is in fact chosen.
                ref={(el) => {
                  if (el)
                    el.indeterminate =
                      approvableSelectedIds.length > 0 &&
                      approvableSelectedIds.length < approvableVisible.length;
                }}
                onChange={(e) =>
                  setSelectedIds(
                    e.target.checked ? new Set(approvableVisible.map((r) => r.id)) : new Set()
                  )
                }
                className="h-3.5 w-3.5 accent-[rgb(var(--accent))]"
                data-testid="bulk-select-all"
              />
              Select all {approvableVisible.length} awaiting approval
            </label>
            <span className="text-xs text-dim">·</span>
            <span className="text-xs text-dim">{approvableSelectedIds.length} selected</span>
            {isLoadingPopularity ? (
              <span className="text-2xs text-dim/70">loading download counts…</span>
            ) : null}
            <div className="ml-auto flex items-center gap-2">
              {bulkResult ? <span className="text-xs text-dim">{bulkResult}</span> : null}
              <Button
                size="sm"
                disabled={approvableSelectedIds.length === 0 || isBulkApproving}
                onClick={() => void handleBulkApprove(approvableSelectedIds)}
                data-testid="bulk-approve-btn"
              >
                {isBulkApproving
                  ? 'Approving…'
                  : `Approve ${approvableSelectedIds.length || ''} selected`.replace('  ', ' ')}
              </Button>
            </div>
          </div>
        ) : null}
        {isRecentOpen ? (
          <div id="recent-package-requests-panel" className="lc-surface overflow-hidden">
            {visibleRequests.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-dim">
                {emptyLabel ?? 'No public repository requests yet.'}
              </div>
            ) : (
              <div className="divide-y divide-rule-soft">
                {visibleRequests.map((request) => {
                  const progressText = getProgressText(request);
                  const timingItems = getRequestTimingItems(request);
                  const packageRoute = getPackageRoute(request);
                  const canonicalName = canonicalPackageName(request.registry, request.name);
                  const isUrl = canonicalName !== request.name;
                  const githubUrl =
                    isUrl && /^https?:\/\/(github|gitlab)\.com/i.test(request.name)
                      ? request.name
                      : null;
                  const displayName = displayPackageName(request.registry, canonicalName);

                  return (
                    <div
                      key={request.id}
                      className="flex flex-col gap-2 px-4 py-3 transition-colors hover:bg-bg sm:flex-row sm:items-start sm:gap-3"
                    >
                      {/* Left: repo name + registry + GitHub link */}
                      <div className="flex min-w-0 flex-1 items-start gap-2.5">
                        {isAdmin && request.status === 'pending_approval' ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(request.id)}
                            onChange={(e) =>
                              setSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(request.id);
                                else next.delete(request.id);
                                return next;
                              })
                            }
                            className="mt-1 h-3.5 w-3.5 flex-shrink-0 accent-[rgb(var(--accent))]"
                            aria-label={`Select ${request.name} for bulk approval`}
                            data-testid="bulk-select-row"
                          />
                        ) : null}
                        <RegistryBadge registry={request.registry} />
                        <div className="min-w-0 flex-1">
                          {packageRoute ? (
                            <button
                              type="button"
                              onClick={() => navigateTo(packageRoute)}
                              className="block max-w-full truncate text-left text-sm font-medium text-ink transition-colors hover:text-accent"
                              title={displayName}
                            >
                              {displayName}
                            </button>
                          ) : (
                            <span
                              className="block max-w-full truncate text-sm font-medium text-ink"
                              title={displayName}
                            >
                              {displayName}
                            </span>
                          )}
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className="font-mono text-xs text-dim">
                              {request.version ?? 'latest'}
                            </span>
                            {isAdmin && request.status !== 'done' && request.status !== 'failed' ? (
                              <PopularityBadge
                                popularity={popularity.get(
                                  popularityKey(
                                    request.registry,
                                    canonicalPackageName(request.registry, request.name)
                                  )
                                )}
                              />
                            ) : null}
                            {githubUrl && (
                              <a
                                href={githubUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-2xs text-accent hover:text-accent/90"
                                title={githubUrl}
                              >
                                {githubUrl.replace(/^https?:\/\//, '')} ↗
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: status + timing + actions */}
                      <div className="flex flex-wrap items-start justify-between gap-2 sm:flex-col sm:items-end sm:gap-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <DuplicateBadge request={request} />
                          <StatusBadge status={request.status} />
                          <span
                            className="text-xs text-dim"
                            title={request.completed_at ?? request.requested_at ?? ''}
                          >
                            {formatIndexedAt(request.completed_at ?? request.requested_at)}
                          </span>
                        </div>
                        {isAdmin && progressText ? (
                          <p
                            className={`text-xs ${request.status === 'failed' ? 'text-del' : 'text-dim'}`}
                          >
                            {progressText}
                          </p>
                        ) : null}
                        {isAdmin && timingItems.length > 0 ? (
                          <p className="text-2xs text-dim">{timingItems.join(' · ')}</p>
                        ) : null}
                        {isAdmin && (
                          <div className="flex items-center gap-1">
                            {request.status === 'pending_approval' && (
                              <button
                                onClick={() => setApproveDialogRequest(request)}
                                disabled={approvingId === request.id || deletingId === request.id}
                                className="rounded px-2 py-0.5 text-xs font-medium text-dim transition-colors hover:bg-good/15 hover:text-good disabled:opacity-40"
                              >
                                {approvingId === request.id ? 'Approving…' : 'Approve'}
                              </button>
                            )}
                            {(request.status === 'pending' || request.status === 'indexing') && (
                              <button
                                onClick={() => void handleStop(request.id)}
                                disabled={stoppingId === request.id || deletingId === request.id}
                                className="rounded px-2 py-0.5 text-xs font-medium text-dim transition-colors hover:bg-warn/15 hover:text-warn disabled:opacity-40"
                              >
                                {stoppingId === request.id ? 'Stopping…' : 'Stop'}
                              </button>
                            )}
                            {request.status === 'failed' && (
                              <button
                                onClick={() => void handleRestart(request.id)}
                                disabled={restartingId === request.id || deletingId === request.id}
                                className="rounded px-2 py-0.5 text-xs font-medium text-dim transition-colors hover:bg-accent-soft hover:text-accent disabled:opacity-40"
                              >
                                {restartingId === request.id ? 'Restarting…' : 'Restart'}
                              </button>
                            )}
                            <button
                              onClick={() => void handleDelete(request.id)}
                              disabled={
                                deletingId === request.id ||
                                restartingId === request.id ||
                                stoppingId === request.id
                              }
                              className="rounded px-2 py-0.5 text-xs font-medium text-dim transition-colors hover:bg-del/10 hover:text-del disabled:opacity-40"
                            >
                              {deletingId === request.id ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {error ? (
        <ErrorDialog title="Request Failed" message={error} onClose={() => setError(null)} />
      ) : null}
      {approveDialogRequest ? (
        <ApproveDialog
          request={approveDialogRequest}
          isApproving={approvingId === approveDialogRequest.id}
          onConfirm={(alias, source, sourceSubpath) =>
            void handleApprove(approveDialogRequest.id, alias, source, sourceSubpath)
          }
          onClose={() => setApproveDialogRequest(null)}
        />
      ) : null}
    </div>
  );
}

export function IndexRepoDialog({
  open,
  onClose,
  onSubmitted,
  initialUrl,
}: {
  open: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
  initialUrl?: string;
}) {
  const { getToken } = useAuth();
  const { activeOrg } = useOrg();
  const [repoUrl, setRepoUrl] = useState('');
  const [repoVersion, setRepoVersion] = useState('');
  const [repoAccessToken, setRepoAccessToken] = useState('');
  // Folder inside the repo to index instead of the whole tree, and the name that
  // folder is stored under — a monolith's modules are named by directory.
  const [repoSubpath, setRepoSubpath] = useState('');
  const [repoPackageName, setRepoPackageName] = useState('');
  const packageNameIsAuto = useRef(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default the package name to the folder's last segment, until it's edited.
  useEffect(() => {
    const trimmed = repoSubpath.trim();
    if (!trimmed || !packageNameIsAuto.current) return;
    setRepoPackageName(subpathLeafName(trimmed));
  }, [repoSubpath]);

  useEffect(() => {
    if (!open) return;
    if (initialUrl) setRepoUrl(initialUrl);
    setRepoVersion('');
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose, initialUrl]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activeOrg) {
      setError('Select an organization before requesting repository indexing.');
      return;
    }
    if (!repoUrl.trim()) {
      setError('Repository URL is required.');
      return;
    }
    const inferredRegistry = inferRepositoryRegistryFromUrl(repoUrl);
    if (!inferredRegistry) {
      setError('Enter a valid GitHub or GitLab repository URL.');
      return;
    }
    if (repoSubpath.trim() && !repoPackageName.trim()) {
      setError('Give the folder a package name so it does not collide with the repository.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('You must be signed in to create org repository requests.');
      await submitOrgRepoIndexRequest(
        activeOrg.id,
        {
          registry: inferredRegistry,
          source_url: repoUrl.trim(),
          version: repoVersion.trim(),
          access_token: repoAccessToken.trim() || undefined,
          source_subpath: repoSubpath.trim() || undefined,
          name: repoSubpath.trim() ? repoPackageName.trim() : undefined,
        },
        token
      );
      setRepoUrl('');
      setRepoVersion('');
      setRepoAccessToken('');
      setRepoSubpath('');
      setRepoPackageName('');
      packageNameIsAuto.current = true;
      setError(null);
      onSubmitted?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to submit repository request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg overflow-hidden border border-rule bg-panel shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-rule-soft px-6 py-4">
          <h2 className="text-base font-semibold text-ink">Index a repository</h2>
          <button onClick={onClose} className="text-dim hover:text-link transition-colors">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="dialog-repo-version" className="text-sm font-medium text-link">
                Branch, tag, or release
              </label>
              <input
                id="dialog-repo-version"
                value={repoVersion}
                onChange={(e) => setRepoVersion(e.target.value)}
                placeholder="e.g. main, v8.0.8, release/2026-04"
                className="lc-input"
                required
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label htmlFor="dialog-repo-url" className="text-sm font-medium text-link">
                Repository URL
              </label>
              <input
                id="dialog-repo-url"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="https://github.com/owner/repo or https://gitlab.com/group/repo"
                className="lc-input"
                autoFocus
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label htmlFor="dialog-repo-token" className="text-sm font-medium text-link">
                Provider token
              </label>
              <input
                id="dialog-repo-token"
                value={repoAccessToken}
                onChange={(e) => setRepoAccessToken(e.target.value)}
                placeholder="Optional for public repos, required for private repos"
                className="lc-input"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="dialog-repo-subpath" className="text-sm font-medium text-link">
                Folder <span className="font-normal text-dim">(optional)</span>
              </label>
              <input
                id="dialog-repo-subpath"
                value={repoSubpath}
                onChange={(e) => setRepoSubpath(e.target.value)}
                placeholder="e.g. services/billing"
                className="lc-input font-mono"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="dialog-repo-package-name" className="text-sm font-medium text-link">
                Package name{' '}
                {repoSubpath.trim() ? (
                  <span className="text-del">*</span>
                ) : (
                  <span className="font-normal text-dim">(folder only)</span>
                )}
              </label>
              <input
                id="dialog-repo-package-name"
                value={repoPackageName}
                onChange={(e) => {
                  packageNameIsAuto.current = false;
                  setRepoPackageName(e.target.value);
                }}
                placeholder="e.g. billing"
                disabled={!repoSubpath.trim()}
                className="lc-input font-mono disabled:opacity-50"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            {repoSubpath.trim() ? (
              <p className="text-xs text-dim sm:col-span-2">
                Only <code className="font-mono">{repoSubpath.trim()}</code> is indexed, stored as
                its own package named{' '}
                <code className="font-mono">{repoPackageName.trim() || '…'}</code>. Index each
                folder separately to keep them side by side.
              </p>
            ) : null}
          </div>

          {error ? <p className="text-sm text-del">{error}</p> : null}

          <div className="flex items-center justify-between gap-3 pt-2">
            <p className="text-xs text-dim">
              Scoped to {activeOrg ? activeOrg.name : 'the active org'} members. Private repos can
              be indexed from a branch, tag, or release.
            </p>
            <div className="flex shrink-0 gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={onClose}
                className="whitespace-nowrap"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={isSubmitting || !repoUrl.trim() || !repoVersion.trim() || !activeOrg}
                className="whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting…' : 'Index repository'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export function OrgRepoRequestsPanel() {
  const { user, getToken } = useAuth();
  const { activeOrg } = useOrg();
  // Any member may re-run a failed request (it is a new request in effect);
  // deleting one manages the org's history, so it is for owners and admins.
  const canManage = canManageOrg(activeOrg);
  const [requests, setRequests] = useState<OrgIndexRequest[]>([]);
  const [restartingId, setRestartingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    if (!user || !activeOrg) {
      setRequests([]);
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        setRequests([]);
        return;
      }
      setRequests(await fetchOrgRepoRequests(activeOrg.id, token));
    } catch {
      // Silently retry on next poll
    }
  }, [activeOrg, getToken, user]);

  useEffect(() => {
    void loadRequests();
    const interval = setInterval(() => void loadRequests(), 5_000);
    return () => clearInterval(interval);
  }, [loadRequests]);

  const handleDelete = async (id: string) => {
    if (!activeOrg) return;
    setDeletingId(id);
    try {
      const token = await getToken();
      if (!token) return;
      await deleteOrgRepoRequest(activeOrg.id, id, token);
      setRequests((prev) => prev.filter((request) => request.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete repository request.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestart = async (id: string) => {
    if (!activeOrg) return;
    setRestartingId(id);
    try {
      const token = await getToken();
      if (!token) return;
      await restartOrgRepoRequest(activeOrg.id, id, token);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restart repository request.');
    } finally {
      setRestartingId(null);
    }
  };

  const activeCount = requests.filter(
    (request) => request.status === 'pending' || request.status === 'indexing'
  ).length;

  return (
    <div className="space-y-3">
      {activeCount > 0 ? (
        <div className="flex justify-end">
          <span className="chip-accent gap-1.5 px-2.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
            {activeCount} active
          </span>
        </div>
      ) : null}
      <div className="lc-surface overflow-hidden">
        {!activeOrg ? (
          <div className="px-5 py-8 text-center text-sm text-dim">
            Select an organization to request and view private GitHub or GitLab repository indexing.
          </div>
        ) : requests.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-dim">
            No repository requests for this organization yet.
          </div>
        ) : (
          <div className="divide-y divide-rule-soft">
            {requests.map((request) => {
              const progressText = getOrgProgressText(request);
              const timingItems = getOrgRequestTimingItems(request);
              const packageRoute = getOrgPackageRoute(request);
              const displayName = displayPackageName(
                request.registry,
                canonicalPackageName(request.registry, request.name)
              );
              const sourceHost = request.source_url.replace(/^https?:\/\//, '');

              return (
                <div
                  key={request.id}
                  className="flex flex-col gap-2 px-4 py-3 transition-colors hover:bg-bg sm:flex-row sm:items-start sm:gap-3"
                >
                  {/* Left: repo name + registry + source link */}
                  <div className="flex min-w-0 flex-1 items-start gap-2.5">
                    <RegistryBadge registry={request.registry} />
                    <div className="min-w-0 flex-1">
                      {packageRoute ? (
                        <button
                          type="button"
                          onClick={() => navigateTo(packageRoute)}
                          className="block max-w-full truncate text-left text-sm font-medium text-ink transition-colors hover:text-accent"
                          title={displayName}
                        >
                          {displayName}
                        </button>
                      ) : (
                        <span
                          className="block max-w-full truncate text-sm font-medium text-ink"
                          title={displayName}
                        >
                          {displayName}
                        </span>
                      )}
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="font-mono text-xs text-dim">
                          {request.version ?? 'default'}
                        </span>
                        <a
                          href={request.source_url}
                          target="_blank"
                          rel="noreferrer"
                          className="max-w-[260px] truncate text-2xs text-accent transition-colors hover:text-accent/90 sm:max-w-[360px]"
                          title={request.source_url}
                        >
                          {sourceHost} ↗
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Right: status + timing + actions */}
                  <div className="flex flex-wrap items-start justify-between gap-2 sm:flex-col sm:items-end sm:gap-1">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={request.status} />
                      <span
                        className="text-xs text-dim"
                        title={request.completed_at ?? request.requested_at ?? ''}
                      >
                        {formatIndexedAt(request.completed_at ?? request.requested_at)}
                      </span>
                    </div>
                    {progressText ? (
                      <p
                        className={`max-w-xs text-xs ${request.status === 'failed' ? 'text-del' : 'text-dim'}`}
                        title={progressText}
                      >
                        {progressText}
                      </p>
                    ) : null}
                    {timingItems.length > 0 ? (
                      <p className="text-2xs text-dim">{timingItems.join(' · ')}</p>
                    ) : null}
                    {user && (
                      <div className="flex items-center gap-1">
                        {request.status === 'failed' ? (
                          <button
                            onClick={() => void handleRestart(request.id)}
                            disabled={restartingId === request.id || deletingId === request.id}
                            className="rounded px-2 py-0.5 text-xs font-medium text-dim transition-colors hover:bg-accent-soft hover:text-accent disabled:opacity-40"
                          >
                            {restartingId === request.id ? 'Restarting…' : 'Restart'}
                          </button>
                        ) : null}
                        {canManage && (
                          <button
                            onClick={() => void handleDelete(request.id)}
                            disabled={deletingId === request.id || restartingId === request.id}
                            className="rounded px-2 py-0.5 text-xs font-medium text-dim transition-colors hover:bg-del/10 hover:text-del disabled:opacity-40"
                          >
                            {deletingId === request.id ? 'Deleting…' : 'Delete'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {error ? (
        <ErrorDialog title="Request Failed" message={error} onClose={() => setError(null)} />
      ) : null}
    </div>
  );
}
